/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Crown, Check, X as XIcon, Star, ArrowRight, Zap, Sparkles, Calendar, Gift, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_ORDER = ['basic', 'pro', 'elite'];

// Theme per tier — Pro gets the gold spotlight (= "Most Popular"), Elite gets
// the deep gradient accent. Basic stays muted to push the eye upward.
const TIER_THEME = {
  basic: {
    border: 'border-white/10',
    accent: 'text-[#6b7d6b]',
    cta: 'btn-ghost',
    cardClass: '',
  },
  pro: {
    border: 'border-[#c8b273]',
    accent: 'text-[#c8b273]',
    cta: 'btn-gold',
    cardClass: 'shadow-[0_0_30px_rgba(200,178,115,0.18)] scale-[1.02]',
  },
  elite: {
    border: 'border-[#7d6730]',
    accent: 'text-[#c8b273]',
    cta: 'btn-ghost-gold',
    cardClass: '',
  },
};

// Comparison-table rows — keeps the value prop scannable. Stable across screens.
const COMPARISON_ROWS = [
  { feature: 'Public community feed & discussions', basic: true, pro: true, elite: true },
  { feature: 'Basic catch uploads', basic: true, pro: true, elite: true },
  { feature: 'View public leaderboards', basic: true, pro: true, elite: true },
  { feature: 'Full detailed lake intel (depth, records, swims)', basic: false, pro: true, elite: true },
  { feature: 'Swim notes & hotspot info', basic: false, pro: true, elite: true },
  { feature: 'Smart bait recommendations', basic: false, pro: true, elite: true },
  { feature: 'Catch analytics & trends', basic: false, pro: true, elite: true },
  { feature: 'Monthly competitions eligibility', basic: false, pro: true, elite: true },
  { feature: 'Catch of the Month submissions', basic: false, pro: true, elite: true },
  { feature: 'Private leagues', basic: false, pro: true, elite: true },
  { feature: 'Pro-only giveaways', basic: false, pro: true, elite: true },
  { feature: 'Early access to new features', basic: false, pro: false, elite: true },
  { feature: 'Exclusive Elite competitions', basic: false, pro: false, elite: true },
  { feature: 'Higher-value monthly giveaways', basic: false, pro: false, elite: true },
  { feature: 'Elite-only private groups', basic: false, pro: false, elite: true },
  { feature: 'Fishery discount partnerships', basic: false, pro: false, elite: true },
  { feature: 'Priority support', basic: false, pro: false, elite: true },
  { feature: 'Elite profile styling & badge', basic: false, pro: false, elite: true },
  { feature: 'Increased upload / media limits', basic: false, pro: false, elite: true },
  { feature: 'Future VIP event access', basic: false, pro: false, elite: true },
];


const Subscription = () => {
  const { user, token, updateUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState('');
  const [trialLoading, setTrialLoading] = useState(false);
  const [tiers, setTiers] = useState(null);
  const [billing, setBilling] = useState('month'); // default to monthly — feels more accessible

  useEffect(() => {
    axios.get(`${API}/subscriptions/tiers`).then((r) => setTiers(r.data)).catch(() => {});
  }, []);

  // ---- Start free trial (self-serve from the Pro card) ----
  const handleStartTrial = async () => {
    if (!user) {
      window.location.href = '/register';
      return;
    }
    setTrialLoading(true);
    try {
      const r = await axios.post(
        `${API}/subscriptions/start-trial`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Pro trial unlocked for ${r.data.trial_days} days — enjoy!`, { duration: 7000 });
      refreshUser?.();
      // Bring them back to the dashboard to see the banner
      setTimeout(() => { window.location.href = '/dashboard'; }, 1200);
    } catch (e) {
      const msg = typeof e.response?.data?.detail === 'string'
        ? e.response.data.detail
        : 'Could not start trial';
      toast.error(msg);
    } finally {
      setTrialLoading(false);
    }
  };

  // ---- Paid subscribe / free switch ----
  const handleSubscribe = async (tierId) => {
    if (tierId === 'basic') {
      if (tierId === user?.subscription_tier) {
        toast.info('You are already on Basic');
        return;
      }
      setLoading(tierId);
      try {
        const response = await axios.post(`${API}/subscriptions/checkout`, {
          package_id: tierId,
          package_type: 'subscription',
          origin_url: window.location.origin,
          billing_interval: 'month',
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (!response.data.redirect) {
          updateUser({ subscription_tier: tierId, billing_interval: 'month' });
          toast.success('Switched to Basic');
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed');
      } finally {
        setLoading('');
      }
      return;
    }

    if (tierId === user?.subscription_tier && (user?.billing_interval || 'month') === billing && !user?.trial_active) {
      toast.info('You are already on this exact plan');
      return;
    }

    setLoading(`${tierId}_${billing}`);
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        package_id: tierId,
        package_type: 'subscription',
        origin_url: window.location.origin,
        billing_interval: billing,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.redirect && response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      const msg = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Failed to process subscription';
      toast.error(msg);
    } finally {
      setLoading('');
    }
  };

  const orderedTiers = tiers
    ? TIER_ORDER.map((id) => ({ id, ...tiers[id] })).filter((t) => t.name)
    : [];

  // Trial-CTA visibility: Pro card shows "Start 3-day free trial" when the user
  // is a fresh Basic with no prior trial usage; otherwise we show the regular
  // subscribe button. Logged-out users get the same CTA — it routes them to /register.
  const canStartTrial = !user || (user.subscription_tier === 'basic' && !user.trial_used);

  return (
    <div className="min-h-screen p-6 md:p-8" data-testid="subscription-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="data-label text-[#c8b273] mb-2">Membership</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white mb-4">
            Pricing built for anglers
          </h1>
          <p className="text-[#8a948a] max-w-2xl mx-auto">
            Start free. Try Pro for 3 days. Cancel anytime. No hidden costs.
          </p>

          {user && (
            <div className="mt-6 inline-flex items-center gap-2 bg-[#141714] px-4 py-2 rounded-lg" data-testid="current-plan-pill">
              <Crown className="w-5 h-5 text-[#c8b273]" />
              <span className="text-white">Current Plan:</span>
              <span className={`font-bold uppercase ${
                user.subscription_tier === 'elite' ? 'text-[#c8b273]' :
                user.subscription_tier === 'pro' ? 'text-[#c8b273]' :
                'text-[#6b7d6b]'
              }`}>
                {user.subscription_tier}
                {user.billing_interval === 'year' ? ' • Yearly' : ''}
                {user.trial_active ? ' • Trial' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Monthly / Yearly toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-[#141714] rounded-lg p-1 border border-white/10" data-testid="billing-toggle">
            <button
              onClick={() => setBilling('month')}
              className={`px-4 py-2 rounded-md text-sm font-medium uppercase tracking-wider transition-colors ${
                billing === 'month' ? 'bg-[#c8b273] text-black' : 'text-[#8a948a] hover:text-white'
              }`}
              data-testid="billing-toggle-month"
            >
              <Calendar className="w-3.5 h-3.5 inline mr-1" />
              Monthly
            </button>
            <button
              onClick={() => setBilling('year')}
              className={`relative px-4 py-2 rounded-md text-sm font-medium uppercase tracking-wider transition-colors ${
                billing === 'year' ? 'bg-[#c8b273] text-black' : 'text-[#8a948a] hover:text-white'
              }`}
              data-testid="billing-toggle-year"
            >
              <Sparkles className="w-3.5 h-3.5 inline mr-1" />
              Yearly
              {billing === 'year' && (
                <span className="absolute -top-2 -right-2 bg-[#22c55e] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                  SAVE 17–18%
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        {!orderedTiers.length ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-card p-6 h-96 animate-pulse">
                <div className="h-6 w-20 bg-[#1f221f] rounded mb-6" />
                <div className="h-10 w-32 bg-[#1f221f] rounded mb-4" />
                <div className="h-4 w-full bg-[#1f221f] rounded mb-2" />
                <div className="h-4 w-3/4 bg-[#1f221f] rounded" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {orderedTiers.map((tier, index) => {
              const theme = TIER_THEME[tier.id];
              const isYearly = billing === 'year' && !tier.is_free;
              const displayPrice = isYearly ? tier.price_yearly : tier.price;
              const periodLabel = tier.is_free ? '' : (isYearly ? '/ year' : '/ month');
              const isCurrent = user?.subscription_tier === tier.id && !user?.trial_active;
              const isPopular = tier.id === 'pro';
              const monthlyEquivalent = isYearly && tier.price_yearly
                ? (tier.price_yearly / 12).toFixed(2)
                : null;
              const showTrialCta = tier.id === 'pro' && canStartTrial;

              return (
                <motion.div
                  key={tier.id}
                  className={`relative glass-card p-6 border-2 ${theme.border} ${theme.cardClass} ${isCurrent ? 'ring-2 ring-[#c8b273]' : ''} flex flex-col`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  data-testid={`tier-card-${tier.id}`}
                >
                  {isPopular && (
                    <div
                      className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c8b273] text-black text-xs font-bold uppercase tracking-widest px-4 py-1 rounded shadow-lg"
                      data-testid="popular-badge"
                    >
                      ★ Most Popular
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute -top-3 right-4 bg-[#22c55e] text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded flex items-center gap-1">
                      <Check className="w-3 h-3" /> Current
                    </div>
                  )}

                  {/* Tier name */}
                  <div className="mb-4">
                    <h2 className={`font-['Barlow_Condensed'] text-2xl font-extrabold uppercase tracking-wider ${theme.accent}`}>
                      {tier.name}
                    </h2>
                  </div>

                  {/* Price */}
                  <div className="mb-1">
                    <span
                      className="font-['Barlow_Condensed'] text-5xl font-extrabold text-white"
                      data-testid={`tier-price-${tier.id}`}
                    >
                      {tier.is_free ? 'Free' : `£${Number(displayPrice).toFixed(2)}`}
                    </span>
                    {!tier.is_free && (
                      <span className="text-[#8a948a] ml-2 text-sm">{periodLabel}</span>
                    )}
                    {tier.is_free && (
                      <span className="text-[#8a948a] ml-2 text-sm">forever</span>
                    )}
                  </div>
                  {monthlyEquivalent ? (
                    <p
                      className="text-[#22c55e] text-xs font-bold mb-4"
                      data-testid={`monthly-equiv-${tier.id}`}
                    >
                      That's just £{monthlyEquivalent}/month, billed yearly
                    </p>
                  ) : (
                    <p className="mb-4 h-4">&nbsp;</p>
                  )}

                  <p className="text-[#8a948a] mb-6 text-sm italic">{tier.tagline}</p>

                  {/* Features */}
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#22c55e] mt-0.5 flex-shrink-0" />
                        <span className="text-[#e2e4e2]">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  {showTrialCta ? (
                    <div className="space-y-2">
                      <Button
                        className="w-full btn-gold"
                        onClick={handleStartTrial}
                        disabled={trialLoading}
                        data-testid="start-trial-btn"
                      >
                        {trialLoading ? 'Starting trial…' : (
                          <>
                            <Gift className="w-4 h-4 mr-2" />
                            Start 3-day Free Trial
                          </>
                        )}
                      </Button>
                      <button
                        type="button"
                        onClick={() => handleSubscribe('pro')}
                        disabled={loading.startsWith('pro')}
                        className="w-full text-xs text-[#8a948a] hover:text-[#c8b273] transition-colors py-1"
                        data-testid="subscribe-direct-pro"
                      >
                        Or skip the trial and subscribe now →
                      </button>
                    </div>
                  ) : (
                    <Button
                      className={`w-full ${isCurrent ? 'btn-ghost cursor-default' : theme.cta}`}
                      onClick={() => handleSubscribe(tier.id)}
                      disabled={
                        loading.startsWith(tier.id) ||
                        (isCurrent && (user?.billing_interval || 'month') === billing)
                      }
                      data-testid={`subscribe-btn-${tier.id}`}
                    >
                      {loading.startsWith(tier.id) ? (
                        'Processing…'
                      ) : isCurrent && (user?.billing_interval || 'month') === billing ? (
                        'Current Plan'
                      ) : tier.is_free ? (
                        user?.subscription_tier === 'basic' ? 'You\'re on Basic' : 'Switch to Basic'
                      ) : (
                        <>
                          {isYearly ? `Get ${tier.name} Yearly` : `Get ${tier.name}`}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}

                  {/* Trust line under CTA */}
                  {!tier.is_free && (
                    <p className="text-[10px] text-[#6b7d6b] text-center mt-3 uppercase tracking-wider">
                      Cancel anytime · Secure checkout
                    </p>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Comparison Table */}
        <div className="mt-20" data-testid="comparison-table">
          <div className="text-center mb-8">
            <p className="data-label text-[#c8b273] mb-2">Compare Plans</p>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">
              Everything in one place
            </h2>
          </div>

          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0b0d0b] border-b border-white/10">
                    <th className="text-left px-4 py-4 text-[#8a948a] font-medium uppercase tracking-wider text-xs">Feature</th>
                    <th className="px-4 py-4 text-center">
                      <div className="text-[#6b7d6b] font-bold uppercase tracking-wider text-xs">Basic</div>
                      <div className="text-white text-xs mt-1">Free</div>
                    </th>
                    <th className="px-4 py-4 text-center bg-[#c8b273]/8 border-x border-[#c8b273]/20">
                      <div className="text-[#c8b273] font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1">
                        <Star className="w-3 h-3" /> Pro
                      </div>
                      <div className="text-white text-xs mt-1">£4.99/mo</div>
                    </th>
                    <th className="px-4 py-4 text-center">
                      <div className="text-[#c8b273] font-bold uppercase tracking-wider text-xs">Elite</div>
                      <div className="text-white text-xs mt-1">£9.99/mo</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr
                      key={row.feature}
                      className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-[#0a0b0a]/40' : ''}`}
                    >
                      <td className="px-4 py-3 text-[#e2e4e2]">{row.feature}</td>
                      <td className="px-4 py-3 text-center">
                        {row.basic ? <Check className="w-4 h-4 text-[#22c55e] inline" /> : <XIcon className="w-4 h-4 text-[#3a3f3a] inline" />}
                      </td>
                      <td className="px-4 py-3 text-center bg-[#c8b273]/5 border-x border-[#c8b273]/10">
                        {row.pro ? <Check className="w-4 h-4 text-[#22c55e] inline" /> : <XIcon className="w-4 h-4 text-[#3a3f3a] inline" />}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.elite ? <Check className="w-4 h-4 text-[#22c55e] inline" /> : <XIcon className="w-4 h-4 text-[#3a3f3a] inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            { icon: Zap, title: 'Instant Access', description: 'Pro features unlock the moment your payment confirms.' },
            { icon: ShieldCheck, title: 'Cancel Anytime', description: 'No contracts, no commitment. Stop renewals with one click.' },
            { icon: Crown, title: 'Community First', description: 'Built by anglers, for anglers — no corporate fluff, just value.' },
          ].map((benefit, index) => (
            <motion.div
              key={benefit.title}
              className="glass-card p-6 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <benefit.icon className="w-10 h-10 text-[#c8b273] mx-auto mb-4" />
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white mb-2">
                {benefit.title}
              </h3>
              <p className="text-[#8a948a] text-sm">{benefit.description}</p>
            </motion.div>
          ))}
        </div>

        {/* FAQ-style reassurance footer */}
        <div className="mt-16 text-center">
          <p className="text-[#8a948a] text-sm">
            Questions? Reply to any of our emails — we read every one. 🎣
          </p>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
